import {
  ConfigurableSSEClientImpl,
  captureConfigureHeaders,
} from "@/shared/services/configurable-sse-client";
import { pollHeadlines, getDelayedHeadlines } from "./api";
import { CursorPoller } from "./CursorPoller";
import keycloakInstance from "@/initKeycloak";
import axios from "axios";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";

/**
 * Normalize URL to be absolute
 * If the URL is already complete (has protocol), return as-is
 * If the URL is relative, prepend window.location.origin
 */
function normalizeUrl(url: string): string {
  try {
    // Try to create URL - if it works, it's already complete
    new URL(url);
    return url;
  } catch {
    // Not a complete URL, prepend origin
    return window.location.origin + url;
  }
}

const baseUrl = import.meta.env.VITE_NOTIFICATIONS_BASE_URL;
// const baseUrl = "https://10.255.255.1"; // Unreachable IP for testing fallback logic

// Normalize endpoints to handle both complete URLs and relative paths
const streamEndpoint = normalizeUrl(baseUrl + "/api/stream");
const configureEndpoint = normalizeUrl(baseUrl + "/api/stream/configure");

// Axios instance for the configure POST — Keycloak interceptor handles auth transparently
const configureClient = axios.create({
  baseURL: configureEndpoint,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

configureClient.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // Refresh if expiring within 30s
    } catch {
      // Best-effort refresh — if it fails, the 401 retry interceptor will handle it
    }
    config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
  }
  return config;
});

attachRetryOn401(configureClient);

const headlinePoller = new CursorPoller(pollHeadlines, 50);

const headlinesSSEClient = new ConfigurableSSEClientImpl({
  url: streamEndpoint,
  connectionName: "Headlines",
  retryEnabled: true,
  cleanupDelayMs: 3000,
  logger: console.log, // Enable for debugging

  // Configure callback — POSTs to the configure endpoint with Keycloak auth.
  // Captures response headers for proxy-detection diagnostics on each call.
  configureFn: async (request) => {
    const response = await configureClient.post("", request);
    captureConfigureHeaders("Headlines", response);
  },


  // HTTP polling fallback — used when SSE is unreliable (e.g. corporate firewalls)
  pollingConfigs: [
    {
      topic: "headline",
      pollingFn: () => headlinePoller.poll(),
      intervalMs: 10000, // 10 seconds — matches Firehose cadence, keeps latency low when stuck in polling
    },
    {
      topic: "headline-delayed",
      // Fetch latest 1 headline — duplicates are deduped by the consumer (useDelayedHeadlines).
      pollingFn: async () => (await getDelayedHeadlines({ limit: 10 })).data,
      intervalMs: 180000, // 3 minutes
    },
  ],
  fallbackThreshold: 5, // Switch to fallback after 5 consecutive SSE failures

  // Proactive periodic reconnect — belt-and-braces defence against silent
  // disconnects (corporate proxies, NAT timeouts, SSL inspection appliances).
  // Cycles the SSE connection every 20 minutes and runs a REST catch-up fetch
  // to cover the brief gap. Dedup by headline id in useHeadlines.ts collapses
  // any overlap with live SSE messages.
  periodicReconnectMs: 20 * 60 * 1000,
  catchUpFns: {
    headline: async () => (await pollHeadlines({ limit: 50 })).data,
  },
});

export default headlinesSSEClient;
